import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  CircularProgress,
  Button,
  TextField,
  MenuItem,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';

import { ContactsContext } from './ContactsContext';
import CustomPropertyModal from './CustomPropertyModal';
import api, { consoleDevLog } from '../apiConfig';
import '../assets/styles/Step1.css';
import noImagePlaceholder from '../components/ComparablesTable/no-photo-or-blank-image-icon-loading-images-or-missing-image-mark-image-not-available-or-image-coming-soon-sign-simple-nature-silhouette-in-frame-isolated-illustration-vector.jpg';

const Step1 = ({
  currentStep,
  client,
  cmaName,
  subjectProperty,
  setClient,
  setCmaName,
  setSubjectProperty,
  handleNextStep,
  handlePreviousStep,
  homes,
  setOverrideSubjectPropertyNum,
}) => {
  const [suggestedHomes, setSuggestedHomes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [homeSearch, setHomeSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [subjectPropertyError, setSubjectPropertyError] = useState(false);
  const [newHomes, setNewHomes] = useState([]);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [previewHome, setPreviewHome] = useState(null);
  const [initialModalData, setInitialModalData] = useState(null);

  const latestSearchRef = useRef('');

  const { contacts } = useContext(ContactsContext);

  const ADD_NEW_PROPERTY = 'ADD_NEW_PROPERTY';

  const handleOpenCustomModal = () => {
    consoleDevLog('handleOpenCustomModal - initialModalData set to:', null);
    setInitialModalData(null);
    setCustomModalOpen(true);
  };

  const handleCloseCustomModal = () => setCustomModalOpen(false);

  const handleSaveCustomProperty = (customPropertyData) => {
    let shouldOverride = false;

    if (initialModalData) {
      const changedKeys = Object.keys(customPropertyData).filter(key =>
        JSON.stringify(customPropertyData[key]) !== JSON.stringify(initialModalData[key])
      );
      const meaningfulChanges = changedKeys.filter(key => !['images', 'imageUrls'].includes(key));
      shouldOverride = meaningfulChanges.length > 0;
    }

    const mergedData = initialModalData
      ? { ...initialModalData, ...customPropertyData }
      : customPropertyData;

    setSubjectProperty(mergedData);
    setSuggestedHomes((prev) => [...prev, mergedData]);
    setHomeSearch(mergedData.full_address || '');
    setSubjectPropertyError(false);
    setIsEditing(false);
    setPreviewHome(mergedData);
    setOverrideSubjectPropertyNum(shouldOverride);
    handleCloseCustomModal();
  };

  const fetchSuggestedHomes = async (inputValue) => {
    try {
      setLoading(true);
      setSuggestedHomes([]);
      
      // Store the current search term
      latestSearchRef.current = inputValue;
      
      const response = await api.get(`/homes/search/${inputValue}`);
      
      // Only update state if this is still the latest search
      if (latestSearchRef.current === inputValue) {
        setNewHomes(response.data);
      }
    } catch (err) {
      // Only log error if this was the latest search
      if (latestSearchRef.current === inputValue) {
        console.error('Failed to fetch homes:', err);
      }
    } finally {
      // Only update loading state if this is still the latest search
      if (latestSearchRef.current === inputValue) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isEditing && homeSearch.length >= 1) {
      clearTimeout(debounceTimeout);
      const timeout = setTimeout(() => {
        fetchSuggestedHomes(homeSearch.trim());
      }, 300);
      setDebounceTimeout(timeout);
      return () => clearTimeout(timeout); // <== important!
    }
  }, [homeSearch, isEditing]);


  useEffect(() => {
    if (!loading && newHomes.length) {
      const limitedHomes = Array.isArray(newHomes) ? newHomes.slice(0, 8) : [];
      setSuggestedHomes([
        ...limitedHomes,
        { full_address: '+ New Custom Property', id: ADD_NEW_PROPERTY }
      ]);
    }
  }, [newHomes, loading]);

  useEffect(() => {
    if (subjectProperty && !isEditing) {
      setHomeSearch(subjectProperty.full_address || '');
    }
    setIsEditing(false);
  }, [subjectProperty]);

  const validateFields = () => {
    if (!subjectProperty) {
      setSubjectPropertyError(true);
      return false;
    }
    return true;
  };

  const handleNextButtonClick = () => {
    if (validateFields()) handleNextStep();
  };

  useEffect(() => {
    setPreviewHome(subjectProperty);
    consoleDevLog('subjectProperty', subjectProperty);
  }, [subjectProperty]);

  const getPreviewImage = (home) => {
    if (!home) return noImagePlaceholder;
    if (home.imageUrls && home.imageUrls.length > 0) return home.imageUrls[0];
    if (home.image) return home.image;
    return noImagePlaceholder;
  };

  const handlePropertySelect = (home) => {
    if (home?.id === ADD_NEW_PROPERTY) {
      handleOpenCustomModal();
      return;
    }
    setSubjectProperty(home);
    setHomeSearch(home.full_address);
    setInitialModalData(home);
    setCustomModalOpen(true);
    setIsEditing(false);
    setOpen(false);
  };

  const optionsWithNewCustom = [...(suggestedHomes || []), { full_address: '+ New Custom Property', id: ADD_NEW_PROPERTY }];

  return (
    <div className="step1-container">
      <div className="step1-customButtonContainer">
        <div className="step1-autocompleteContainer">
          <Autocomplete
            id="subjectProperty"
            freeSolo
            options={suggestedHomes}
            value={subjectProperty || null}
            inputValue={homeSearch}
            getOptionLabel={(option) => option?.full_address || ''}
            getOptionSelected={(option, value) => option.full_address === value.full_address}
            style={{ flex: 1 }}
            onInputChange={(e, val) => {
              if (val === '') {
                setSubjectProperty(null);
                setHomeSearch('');
                setIsEditing(false);
                setInitialModalData(null);
              } else {
                setHomeSearch(val);
                setIsEditing(true);
                setOpen(true);
                setSubjectPropertyError(false);
              }
            }}
            onChange={(e, home) => {
              if (home?.id === ADD_NEW_PROPERTY) {
                setHomeSearch(''); 
                setSubjectProperty(null); 
                setInitialModalData(null);
                handleOpenCustomModal();
                return;
              }

              if (home) {
                handlePropertySelect(home);
              }
            }}
            open={open && !loading && isEditing}
            onBlur={() => setOpen(false)}
            filterOptions={(options) => {
              const filtered = [...options];

              // Only add if not already included
              if (!filtered.some((o) => o.id === ADD_NEW_PROPERTY)) {
                filtered.push({
                  id: ADD_NEW_PROPERTY,
                  full_address: '+ New Custom Property',
                });
              }

              return filtered;
            }}
            renderOption={(option) => (
              <div className={option.id === ADD_NEW_PROPERTY ? 'add-new-menu-item' : ''}>
                {option.full_address}
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for Subject Property (Autofill)"
                variant="outlined"
                required
                fullWidth
                autoFocus
                error={subjectPropertyError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />


          {subjectProperty?.full_address && (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              className="step1-editButton"
              onClick={() => handlePropertySelect(subjectProperty)}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {!customModalOpen && previewHome?.full_address && (
        <div className="step1-preview-container">
          <div className="step1-preview-card">
            <div className="step1-preview-left">
              <div className="step1-property-labels">
                <h2>{previewHome.full_address}</h2>
                <p>
                  {[
                    previewHome.Br ? `${previewHome.Br} beds` : null,
                    previewHome.Bath_tot ? `${previewHome.Bath_tot} baths` : null,
                    previewHome.Type_own1_out || null,
                    previewHome.Ml_num || null,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
              </div>
              <div className="step1-map-container">
                <iframe
                  title="map"
                  className="step1-preview-map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    previewHome.full_address,
                  )}&output=embed`}
                  frameBorder="0"
                />
              </div>
            </div>
            <div className="step1-preview-right">
              <div className="step1-preview-image-container">
                <img
                  src={getPreviewImage(previewHome)}
                  alt="Selected property"
                  className="step1-preview-image"
                  onError={(e) => {
                    e.target.src = noImagePlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="step1-buttonContainer">
        {subjectProperty && (
          <Button variant="contained" color="primary" onClick={handleNextButtonClick}>
            Next
          </Button>
        )}
        {currentStep > 1 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePreviousStep}
            className="step1-previous"
          >
            Previous
          </Button>
        )}
      </div>

      <CustomPropertyModal
        open={customModalOpen}
        onClose={handleCloseCustomModal}
        onSave={handleSaveCustomProperty}
        initialData={initialModalData}
      />
    </div>
  );
};

export default Step1;
